import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { apiJson } from '@/api/client'

/**
 * AF-124 — Bank picker autocomplete data hooks.
 *
 * Two queries:
 *   - useBanks(term, opts): server-side search; key `['banks', term, opts]`.
 *     Fires only when `term.length >= 2` so we don't hammer the API on
 *     single-character inputs. Cached 30 minutes per the AC.
 *   - useBank(code): resolve a single bank by CBN code; key
 *     `['banks', 'by-code', code]`. Used to render the trigger label when
 *     the picker is opened with a pre-selected value (e.g. on edit forms).
 *
 * The full bank list is *not* fetched on mount — Nigeria has 250+ regulated
 * institutions and shipping all of them on every page load wastes bytes.
 */

export interface Bank {
  cbn_code: string
  short_name: string
  name: string
  nip_enabled: boolean
  rtgs_enabled: boolean
}

interface BanksResponse {
  banks: Bank[]
}

export interface BankSearchOptions {
  /** Restrict results to banks that participate in NIP. */
  onlyNipEnabled?: boolean
  /** Restrict results to banks that participate in RTGS. */
  onlyRtgsEnabled?: boolean
}

const STALE_MS = 30 * 60 * 1000 // 30 minutes — banks list is reference data.

export function useBanks(term: string, opts: BankSearchOptions = {}) {
  const trimmed = term.trim()
  const enabled = trimmed.length >= 2
  return useQuery({
    queryKey: ['banks', trimmed, opts] as const,
    queryFn: () =>
      apiJson<BanksResponse>('/banks', {
        searchParams: {
          q: trimmed,
          nip_enabled: opts.onlyNipEnabled ? true : undefined,
          rtgs_enabled: opts.onlyRtgsEnabled ? true : undefined,
        },
      }),
    enabled,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  })
}

/**
 * Resolve a single bank by CBN code. Used for trigger labels when the picker
 * mounts with a pre-selected value but the bank is not in the search-result
 * cache yet.
 */
export function useBank(cbnCode: string | null) {
  return useQuery({
    queryKey: ['banks', 'by-code', cbnCode] as const,
    queryFn: () => apiJson<Bank>(`/banks/${cbnCode}`),
    enabled: cbnCode !== null && cbnCode !== '',
    staleTime: STALE_MS,
  })
}
